import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Calendar, Clock } from "lucide-react";
import { RazorRadioCheck, RazorToggleSlide } from "../../../../components/razor-reuse";
import { Button } from "../../../../components/custom/custom-button";
import RichTextEditor from "../../../../components/fragment/rich-text-editor/RichTextEditor";
import type { AppDispatch } from "../../../../redux/store";
import RootState from "../../../../redux/type";
import { stripHtml } from "../../../../utils/Helper";
import {
  downloadNotificationAudienceTemplate,
  previewNotificationAudience,
  uploadNotificationAudienceCsv,
} from "../../../../redux/notificationCampaign";
import {
  CAMPAIGN_TYPE_OPTIONS,
  CreateNotificationForm,
  FilterRuleForm,
  isEmailOnlyChannelSelection,
  NotificationAudienceType,
  NotificationRecurrenceFrequency,
  NotificationScheduleType,
} from "../types";
import AudienceFilterRules, { isRuleValueEmpty } from "./AudienceFilterRules";
import CsvDropzone from "./CsvDropzone";
import WizardActionBar from "./WizardActionBar";

const TITLE_MAX_LENGTH = 80;
const BODY_MAX_LENGTH = 250;
const BODY_WARNING_THRESHOLD = 235;
const AUDIENCE_PREVIEW_DEBOUNCE_MS = 500;

const AUDIENCE_OPTIONS: {
  mode: NotificationAudienceType;
  label: string;
  description: string;
}[] = [
  { mode: "all", label: "All eligible users", description: "Send to your entire active user base" },
  { mode: "filter", label: "Filter by criteria", description: "Target specific user segments" },
  { mode: "csv_upload", label: "Upload user list", description: "CSV with specific user IDs" },
];

const SCHEDULE_OPTIONS: {
  mode: NotificationScheduleType;
  label: string;
  description: string;
}[] = [
  { mode: "immediate", label: "Send immediately", description: "Dispatched as soon as you confirm" },
  { mode: "scheduled", label: "Schedule for later", description: "Pick a specific date and time" },
];

const RECURRENCE_OPTIONS: { value: NotificationRecurrenceFrequency; label: string }[] = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
];

function SectionHeader({ number, title }: { number: string; title: string }) {
  return (
    <div className="flex items-center gap-[10px] px-[24px] py-[16px] border-b border-[#E4E4E7]">
      <span className="flex items-center justify-center h-[22px] min-w-[22px] rounded-[6px] bg-[#F5F0FA] px-[6px] text-[11px] font-bold text-[#734792]">
        {number}
      </span>
      <p className="text-[14px] font-bold text-[#1E1E1E]">{title}</p>
    </div>
  );
}

function RadioCard({
  id,
  label,
  description,
  selected,
  onSelect,
}: {
  id: number;
  label: string;
  description: string;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <label
      className={`flex items-center gap-[12px] rounded-[8px] border p-[14px] cursor-pointer ${
        selected ? "border-[#734792] bg-[#F9F6FC]" : "border-[#E4E4E7]"
      }`}
    >
      <RazorRadioCheck id={id} color="black-light" checked={selected} onChange={onSelect} />
      <p className="text-[14px] text-[#1E1E1E]">
        <span className="font-bold">{label}</span>{" "}
        <span className="text-[#758089]">{description}</span>
      </p>
    </label>
  );
}

interface ConfigureStepProps {
  form: CreateNotificationForm;
  onChange: <K extends keyof CreateNotificationForm>(
    field: K,
    value: CreateNotificationForm[K],
  ) => void;
  onCancel: () => void;
  onSaveDraft: () => void;
  onContinue: () => void;
}

export default function ConfigureStep({
  form,
  onChange,
  onCancel,
  onSaveDraft,
  onContinue,
}: ConfigureStepProps) {
  const dispatch = useDispatch<AppDispatch>();
  const {
    audience_preview_count,
    loading_audience_preview,
    loading_audience_csv,
    audience_csv_result,
    downloading_audience_template,
  } = useSelector((state: RootState) => state.notification_campaign);

  // The audience/preview endpoint takes a single `recipient_type`, but this
  // wizard collects one per channel — use the first selected channel's as a
  // reasonable stand-in for the live estimate shown here.
  const previewRecipientType = form.channels[0]?.recipient_type || "both";

  useEffect(() => {
    if (form.audience_type !== "filter" || form.filter_rules.length < 1) return;
     // Only preview when every filter rule has both a type and a value.
  const hasIncompleteRule = form.filter_rules.some(
    (rule) => !rule.type || isRuleValueEmpty(rule.value),
  );

  if (hasIncompleteRule) {
    return;
  }
    const timeout = setTimeout(() => {
      dispatch(
        previewNotificationAudience({
          recipient_type: previewRecipientType,
          audience_type: "filter",
          filter_rules: form.filter_rules.map((rule) => ({
            type: rule.type.toLowerCase(),
            value: rule.value,
          })),
        }) as any,
      );
    }, AUDIENCE_PREVIEW_DEBOUNCE_MS);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.audience_type, form.filter_rules, previewRecipientType]);

  const handleCsvSelected = (file: File) => {
    onChange("uploaded_file_name", file.name);
    (dispatch(uploadNotificationAudienceCsv(file) as any) as Promise<any>).then(
      (result: any) => {
        const matchedIds = result?.payload?.data?.data?.matched_user_ids;
        if (matchedIds) {
          onChange("csv_matched_user_ids", matchedIds);
        }
      },
    );
  };

  const handleDownloadTemplate = () => {
    dispatch(downloadNotificationAudienceTemplate({}) as any);
  };

  const isEmailOnly = isEmailOnlyChannelSelection(form.channels);
  const bodyPlainText = useMemo(() => stripHtml(form.body), [form.body]);

  const canContinue =
    !!form.type &&
    form.channels.length > 0 &&
    form.title.trim().length > 0 &&
    (isEmailOnly
      ? bodyPlainText.trim().length > 0
      : form.body.trim().length > 0 && form.body.length <= BODY_MAX_LENGTH) &&
    (form.schedule_type === "immediate" ||
      (!!form.schedule_date && !!form.schedule_time));

  return (
    <div className="mt-[16px]">
      <p className="text-[12px] font-semibold tracking-wide text-[#734792] uppercase">
        Step 2 of 4
      </p>
      <p className="text-[20px] font-bold text-[#1E1E1E] mt-[4px]">
        Configure the notification
      </p>

      <div className="mt-[24px] space-y-[16px]">
        {/* 01 message content */}
        <div className="bg-white border border-[#E4E4E7] rounded-[8px]">
          <SectionHeader number="01" title="Message content" />
          <div className="p-[24px] space-y-[20px]">
            <div>
              <label className="text-[13px] font-medium text-[#1E1E1E]">
                Notification type <span className="text-[#DC2626]">*</span>
              </label>
              <select
                value={form.type}
                onChange={(e) =>
                  onChange("type", e.target.value as CreateNotificationForm["type"])
                }
                className="mt-[8px] h-[40px] w-full rounded-[8px] border border-[#E4E4E7] px-[12px] text-[14px] text-[#1E1E1E] outline-none focus:border-[#734792]"
              >
                <option value="" disabled>
                  Select a notification type
                </option>
                {CAMPAIGN_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[13px] font-medium text-[#1E1E1E]">
                Notification title <span className="text-[#DC2626]">*</span>
              </label>
              <input
                type="text"
                maxLength={TITLE_MAX_LENGTH}
                placeholder="e.g. Your account has been verified"
                value={form.title}
                onChange={(e) => onChange("title", e.target.value)}
                className="mt-[8px] h-[40px] w-full rounded-[8px] border border-[#E4E4E7] px-[12px] text-[14px] text-[#1E1E1E] outline-none focus:border-[#734792]"
              />
              <p className="mt-[4px] text-right text-[12px] text-[#A1A1AA]">
                {form.title.length}/{TITLE_MAX_LENGTH}
              </p>
            </div>
            <div>
              <label className="text-[13px] font-medium text-[#1E1E1E]">
                Message body <span className="text-[#DC2626]">*</span>
              </label>
              {isEmailOnly ? (
                <div className="mt-[8px]">
                  <RichTextEditor
                    value={form.body}
                    onChange={(html) => onChange("body", html)}
                    placeholder="Hi , we wanted to let you know..."
                  />
                </div>
              ) : (
                <>
                  <textarea
                    maxLength={BODY_MAX_LENGTH}
                    rows={4}
                    placeholder="Hi , we wanted to let you know..."
                    value={form.body}
                    onChange={(e) => onChange("body", e.target.value)}
                    className="mt-[8px] w-full rounded-[8px] border border-[#E4E4E7] px-[12px] py-[10px] text-[14px] text-[#1E1E1E] outline-none focus:border-[#734792] resize-none"
                  />
                  <p
                    className={`mt-[4px] text-right text-[12px] ${
                      form.body.length >= BODY_MAX_LENGTH
                        ? "text-[#DC2626]"
                        : form.body.length >= BODY_WARNING_THRESHOLD
                          ? "text-[#D97706]"
                          : "text-[#A1A1AA]"
                    }`}
                  >
                    {form.body.length}/{BODY_MAX_LENGTH}
                  </p>
                </>
              )}
            </div>
          </div>
        </div>

        {/* 02 action button */}
        <div className="bg-white border border-[#E4E4E7] rounded-[8px]">
          <SectionHeader number="02" title="Action button" />
          <div className="p-[24px] space-y-[16px]">
            <div className="flex items-center gap-[12px]">
              <RazorToggleSlide
                id="cta-toggle"
                color="black-light"
                checked={form.action_button_enabled}
                onChange={() =>
                  onChange("action_button_enabled", !form.action_button_enabled)
                }
              />
              <p className="text-[14px] text-[#1E1E1E]">
                {form.action_button_enabled
                  ? "Action button enabled"
                  : "Add a CTA button with a deep link or URL"}
              </p>
            </div>

            {!form.action_button_enabled ? (
              <p className="text-[13px] text-[#A1A1AA]">
                No action button — users will receive the message only.
              </p>
            ) : (
              <div className="space-y-[16px]">
                <div>
                  <label className="text-[13px] font-medium text-[#1E1E1E]">
                    Button label
                  </label>
                  <input
                    type="text"
                    maxLength={50}
                    placeholder="Complete KYC"
                    value={form.action_button_label}
                    onChange={(e) => onChange("action_button_label", e.target.value)}
                    className="mt-[8px] h-[40px] w-full rounded-[8px] border border-[#E4E4E7] px-[12px] text-[14px] text-[#1E1E1E] outline-none focus:border-[#734792]"
                  />
                </div>
                <div>
                  <label className="text-[13px] font-medium text-[#1E1E1E]">
                    Deep link / URL
                  </label>
                  <input
                    type="text"
                    maxLength={2048}
                    placeholder="app://kyc or https://app.example.com/kyc"
                    value={form.action_button_url}
                    onChange={(e) => onChange("action_button_url", e.target.value)}
                    className="mt-[8px] h-[40px] w-full rounded-[8px] border border-[#E4E4E7] px-[12px] text-[14px] text-[#1E1E1E] outline-none focus:border-[#734792]"
                  />
                </div>
                {form.action_button_label && (
                  <div>
                    <p className="text-[11px] font-semibold tracking-wide text-[#71717A] uppercase mb-[8px]">
                      Preview
                    </p>
                    <Button size="sm">{form.action_button_label}</Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* 03 target audience */}
        <div className="bg-white border border-[#E4E4E7] rounded-[8px]">
          <SectionHeader number="03" title="Target audience" />
          <div className="p-[24px]">
            <div className="space-y-[10px]">
              {AUDIENCE_OPTIONS.map((option, index) => (
                <RadioCard
                  key={option.mode}
                  id={index}
                  label={option.label}
                  description={option.description}
                  selected={form.audience_type === option.mode}
                  onSelect={() => onChange("audience_type", option.mode)}
                />
              ))}
            </div>

            {form.audience_type === "filter" && (
              <AudienceFilterRules
                rules={form.filter_rules}
                onChange={(rules: FilterRuleForm[]) => onChange("filter_rules", rules)}
                matchedCount={audience_preview_count}
                loadingMatchedCount={loading_audience_preview}
              />
            )}

            {form.audience_type === "csv_upload" && (
              <div className="mt-[16px]">
                <CsvDropzone
                  fileName={form.uploaded_file_name}
                  onFileSelected={handleCsvSelected}
                  uploading={loading_audience_csv}
                  matchedCount={audience_csv_result?.matched_user_ids?.length}
                  unmatchedCount={audience_csv_result?.unmatched_emails?.length}
                  onDownloadTemplate={handleDownloadTemplate}
                  downloadingTemplate={downloading_audience_template}
                />
              </div>
            )}
          </div>
        </div>

        {/* 04 delivery schedule */}
        <div className="bg-white border border-[#E4E4E7] rounded-[8px]">
          <SectionHeader number="04" title="Delivery schedule" />
          <div className="p-[24px]">
            <div className="space-y-[10px]">
              {SCHEDULE_OPTIONS.map((option, index) => (
                <RadioCard
                  key={option.mode}
                  id={index + 10}
                  label={option.label}
                  description={option.description}
                  selected={form.schedule_type === option.mode}
                  onSelect={() => onChange("schedule_type", option.mode)}
                />
              ))}
            </div>

            {form.schedule_type === "scheduled" && (
              <div className="mt-[16px] space-y-[16px]">
                <div className="grid grid-cols-2 gap-[16px]">
                  <div>
                    <label className="text-[13px] font-medium text-[#1E1E1E]">Date</label>
                    <div className="relative mt-[8px]">
                      <input
                        type="date"
                        value={form.schedule_date}
                        onChange={(e) => onChange("schedule_date", e.target.value)}
                        className="h-[40px] w-full rounded-[8px] border border-[#E4E4E7] pl-[12px] pr-[36px] text-[14px] text-[#1E1E1E] outline-none focus:border-[#734792]"
                      />
                      <Calendar className="pointer-events-none absolute right-[12px] top-1/2 -translate-y-1/2 size-[16px] text-[#A1A1AA]" />
                    </div>
                  </div>
                  <div>
                    <label className="text-[13px] font-medium text-[#1E1E1E]">
                      Time (UTC)
                    </label>
                    <div className="relative mt-[8px]">
                      <input
                        type="time"
                        value={form.schedule_time}
                        onChange={(e) => onChange("schedule_time", e.target.value)}
                        className="h-[40px] w-full rounded-[8px] border border-[#E4E4E7] pl-[12px] pr-[36px] text-[14px] text-[#1E1E1E] outline-none focus:border-[#734792]"
                      />
                      <Clock className="pointer-events-none absolute right-[12px] top-1/2 -translate-y-1/2 size-[16px] text-[#A1A1AA]" />
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <p className="text-[14px] text-[#1E1E1E]">Set it as recurrent notification</p>
                  <RazorToggleSlide
                    id="recurrence-toggle"
                    color="black-light"
                    checked={form.is_recurring}
                    onChange={() => onChange("is_recurring", !form.is_recurring)}
                  />
                </div>

                <div
                  className={`flex items-center gap-[20px] ${
                    !form.is_recurring ? "opacity-40 pointer-events-none" : ""
                  }`}
                >
                  {RECURRENCE_OPTIONS.map((option, index) => (
                    <label key={option.value} className="flex items-center gap-[8px]">
                      <RazorRadioCheck
                        id={index + 20}
                        color="black-light"
                        checked={form.recurrence_frequency === option.value}
                        onChange={() => onChange("recurrence_frequency", option.value)}
                      />
                      <span className="text-[13px] text-[#1E1E1E]">{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <WizardActionBar
        onCancel={onCancel}
        onSaveDraft={onSaveDraft}
        onPrimary={onContinue}
        primaryLabel="Continue"
        primaryDisabled={!canContinue}
      />
    </div>
  );
}
